import { useAtomValue } from 'jotai';
import { domain, State } from 'modules/data';
import { useCalculatedAmount } from 'pages/requestForm/containers/netsuite/hooks';
import { netSuiteBillFieldValuesState } from 'pages/requestForm/containers/netsuite/NetSuiteBillRequestForm/NetSuiteBillRequestForm.states';
import { NetSuiteBillFields } from 'pages/requestForm/containers/netsuite/NetSuiteBillRequestForm/NetSuiteBillRequestForm.types';
import { requestSelector } from 'pages/requestForm/selectors/pageSelectors';
import {
    getQBooksAccountLineItemsSummary,
    getQBooksLineItemsSummary,
    mergeSummaries,
} from 'pages/requestForm/selectors/qbooks';
import { getXeroLineItemsSummary } from 'pages/requestForm/selectors/xero';
import { useSelector } from 'react-redux';
import {
    useGetNetSuiteRequestsDuplicates,
    useGetQBooksRequestsDuplicates,
    useGetXeroRequestsDuplicates,
} from 'shared/data/webApp/v2';

import {
    ParamsNetSuiteBill as RequestDataNetSuiteBill,
    ParamsQbooksBill as RequestDataQbooksBill,
    ParamsXeroBill as RequestDataXeroBill,
} from './CheckDuplicates.types';

const requestDataMapper = {
    [domain.IntegrationCode.QBooksBill]: (state: State, request: domain.QBooksBillRequest): RequestDataQbooksBill => {
        const { companyId, integrationCode, details } = request;

        const summary = mergeSummaries(
            getQBooksLineItemsSummary(state, request),
            getQBooksAccountLineItemsSummary(state, request)
        );

        return {
            params: {
                path: {
                    companyId,
                },
                query: {
                    amount: summary.totalAmount,
                    currency: request.currency,
                    date: details.date,
                    vendorId: details.vendor?.id,
                    integrationCode,
                    invoiceNumber: request.details.number,
                },
            },
        };
    },
    [domain.IntegrationCode.XeroBill]: (state: State, request: domain.XeroBillRequest): RequestDataXeroBill => {
        const { companyId, integrationCode, details } = request;

        return {
            params: {
                path: {
                    companyId,
                },
                query: {
                    amount: getXeroLineItemsSummary(state, request).totalAmount,
                    counterPartyId: details.contact?.id,
                    currency: request.currency,
                    date: details.date,
                    integrationCode,
                    invoiceNumber: request.details.reference,
                },
            },
        };
    },
    [domain.IntegrationCode.NetSuiteBill]: (
        request: domain.NetSuiteBillRequest,
        fields: Partial<NetSuiteBillFields>,
        totalAmount: number
    ): RequestDataNetSuiteBill => {
        const { companyId, integrationCode } = request;

        return {
            params: {
                path: {
                    companyId,
                },
                query: {
                    amount: totalAmount,
                    vendorId: fields.vendor?.id,
                    currency: fields.currency?.id,
                    date: fields.date,
                    reference: fields.reference,
                    integrationCode,
                },
            },
        };
    },
};

export const useRequestDuplicatesQbooksBill = () => {
    const request = <domain.QBooksBillRequest>useSelector(requestSelector);

    const { params, queryOptions } = useSelector((state: State) =>
        requestDataMapper[domain.IntegrationCode.QBooksBill](state, request)
    );

    return useGetQBooksRequestsDuplicates(params, queryOptions);
};

export const useRequestDuplicatesXeroBill = () => {
    const request = <domain.XeroBillRequest>useSelector(requestSelector);

    const { params, queryOptions } = useSelector((state: State) =>
        requestDataMapper[domain.IntegrationCode.XeroBill](state, request)
    );

    return useGetXeroRequestsDuplicates(params, queryOptions);
};

export const useRequestDuplicatesNetSuiteBill = () => {
    const fields = useAtomValue(netSuiteBillFieldValuesState);

    const { totalAmount } = useCalculatedAmount(fields.currency);

    const request = <domain.NetSuiteBillRequest>useSelector(requestSelector);

    const { params, queryOptions } = requestDataMapper[domain.IntegrationCode.NetSuiteBill](
        request,
        fields,
        totalAmount
    );

    return useGetNetSuiteRequestsDuplicates(params, queryOptions);
};
